import { Paper, Stack, Tabs } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import { isArray } from 'lodash';
import * as React from 'react';
import { CollapsibleSection } from '../../../../../../react-commons/components/data-display/CollapsibleSection';
import { getColorMantine } from '../../../../../../react-commons/components/inputs/text-fields/types';
import { ValidationEntrySeverity } from '../../../../../../react-commons/components/ValidationEntrySeverity';
import { formatToPascalCase } from '../../../../../../react-commons/utils/utilFunctions';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import { DetailedValidationResource } from '../../../../resource/resources/validation/detailedValidationResource';
import { getResourceTypeShortLabel } from '../../../util/utilFunctions';
import { ResourceDetailsComponent } from '../../validation/ResourceDetailsComponent';
import { ValidationResourceResultComponent } from './ValidationResourceResultComponent';

export const ResourceDetailsWithMultiValidationDialogContent= ({ resourceValidations, resourceType }: {
    resourceValidations?: DetailedValidationResource[] | null;
    resourceType: ResourceType | ResourceType[];
}) => {
    const filtered = resourceValidations?.filter((value) => value.maxSeverity !== undefined) ?? [];
    const [selectedTab, setSelectedTab] = React.useState<string | null>(
        resourceValidations && filtered.length ? 'Validation'
        : isArray(resourceType) ? resourceType[0]
        : resourceType
    );

    const getMaxSeverity = (): ValidationEntrySeverity | undefined => {
        if (filtered.some((value) => value.maxSeverity === ValidationEntrySeverity.FATAL)) {
            return ValidationEntrySeverity.FATAL;
        } else if (filtered.some((value) => value.maxSeverity === ValidationEntrySeverity.CRITICAL)) {
            return ValidationEntrySeverity.CRITICAL;
        } else if (filtered.some((value) => value.maxSeverity === ValidationEntrySeverity.WARNING)) {
            return ValidationEntrySeverity.WARNING;
        } else if (filtered.some((value) => value.maxSeverity === ValidationEntrySeverity.NOTE)) {
            return ValidationEntrySeverity.NOTE;
        }
        return undefined;
    };

    const getLabel = (type: ResourceType | undefined): string => {
        return `${type ? getResourceTypeShortLabel(type) : 'Resource'} Info`;
    };

    if (resourceValidations && filtered.length && isArray(resourceType)) {
        return (
            <div style={{ minWidth: 1200 }}>
                <Tabs value={selectedTab} onChange={setSelectedTab}>
                    <Tabs.List>
                        <Tabs.Tab
                            value={'Validation'}
                            style={{ height: 28, minHeight: 28 }}
                            rightSection={<IconExclamationCircle color={getColorMantine(getMaxSeverity())} />}
                        >
                            Validation
                        </Tabs.Tab>
                        {resourceType.map((value) => (
                            <Tabs.Tab key={value} style={{ height: 28, minHeight: 28 }} value={value}>
                                {getLabel(value)}
                            </Tabs.Tab>
                        ))}
                    </Tabs.List>
                    <Tabs.Panel value={'Validation'} mih={550}>
                        {filtered.length === 1 && (
                            <ValidationResourceResultComponent
                                maxSeverity={filtered[0].maxSeverity ?? ValidationEntrySeverity.NOTE}
                                resourceValidation={filtered[0]}
                                persistableTypeGroup={filtered[0].resourceType}
                            />
                        )}
                        {filtered.length > 1 && (
                            <Stack>
                                {filtered.map((value, index) => (
                                    <CollapsibleSection
                                        key={index}
                                        label={formatToPascalCase(value.resourceType)}
                                        labelBarNode={
                                            <IconExclamationCircle color={getColorMantine(value.maxSeverity)} />
                                        }
                                        initialOpen={true}
                                    >
                                        <Paper withBorder>
                                            <ValidationResourceResultComponent
                                                maxSeverity={value.maxSeverity ?? ValidationEntrySeverity.NOTE}
                                                resourceValidation={value}
                                                persistableTypeGroup={value.resourceType}
                                            />
                                        </Paper>
                                    </CollapsibleSection>
                                ))}
                            </Stack>
                        )}
                    </Tabs.Panel>
                    {resourceType.map((value) => (
                        <Tabs.Panel key={value} value={value} mih={550}>
                            <ResourceDetailsComponent resourceType={value} />
                        </Tabs.Panel>
                    ))}
                </Tabs>
            </div>
        );
    } else if (resourceValidations && filtered.length && !isArray(resourceType)) {
        return (
            <div style={{ minWidth: 1200 }}>
                <Tabs value={selectedTab} onChange={setSelectedTab}>
                    <Tabs.List>
                        <Tabs.Tab
                            value={'Validation'}
                            style={{ height: 28, minHeight: 28 }}
                            rightSection={<IconExclamationCircle color={getColorMantine(getMaxSeverity())} />}
                        >
                            Validation
                        </Tabs.Tab>
                        <Tabs.Tab style={{ height: 28, minHeight: 28 }} value={resourceType}>
                            {getLabel(resourceType)}
                        </Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value={'Validation'} mih={550}>
                        {filtered.length === 1 && (
                            <ValidationResourceResultComponent
                                maxSeverity={filtered[0].maxSeverity ?? ValidationEntrySeverity.NOTE}
                                resourceValidation={filtered[0]}
                                persistableTypeGroup={filtered[0].resourceType}
                            />
                        )}
                        {filtered.length > 1 && (
                            <Stack>
                                {filtered.map((value, index) => (
                                    <CollapsibleSection
                                        key={index}
                                        label={formatToPascalCase(value.resourceType)}
                                        labelBarNode={
                                            <IconExclamationCircle color={getColorMantine(value.maxSeverity)} />
                                        }
                                        initialOpen={true}
                                    >
                                        <Paper withBorder>
                                            <ValidationResourceResultComponent
                                                maxSeverity={value.maxSeverity ?? ValidationEntrySeverity.NOTE}
                                                resourceValidation={value}
                                                persistableTypeGroup={value.resourceType}
                                            />
                                        </Paper>
                                    </CollapsibleSection>
                                ))}
                            </Stack>
                        )}
                    </Tabs.Panel>
                    <Tabs.Panel value={resourceType} mih={550}>
                        <ResourceDetailsComponent resourceType={resourceType} />
                    </Tabs.Panel>
                </Tabs>
            </div>
        );
    }

    if (isArray(resourceType)) {
        return (
            <div style={{ minWidth: 1200 }}>
                <Tabs value={selectedTab} onChange={setSelectedTab}>
                    <Tabs.List>
                        {resourceType.map((value) => (
                            <Tabs.Tab key={value} style={{ height: 28, minHeight: 28 }} value={value}>
                                {getLabel(value)}
                            </Tabs.Tab>
                        ))}
                    </Tabs.List>
                    {resourceType.map((value) => (
                        <Tabs.Panel key={value} value={value} mih={550}>
                            <ResourceDetailsComponent resourceType={value} />
                        </Tabs.Panel>
                    ))}
                </Tabs>
            </div>
        );
    }

    return (
        <div style={{ minWidth: 1200 }}>
            <ResourceDetailsComponent resourceType={resourceType} />
        </div>
    );
};


